import { CenterChildren } from '@editor/components/utilities/layouts';
import { QUERIES } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';
import styled, { css } from 'styled-components';

import { PricingPlanProps } from './types';

const getBorder = (hasLabel: boolean): string => {
  if (hasLabel) {
    return `2px solid ${themeStyle.highlight}`;
  } else {
    return `2px solid ${themeStyle.varInkLight}`;
  }
};

export const PlanContainer = styled.div<{
  hasLabel: boolean;
  state: PricingPlanProps['state'];
  isTeamsContainer?: boolean;
  height?: string;
  businessPlanIsEnabled?: boolean;
}>`
  width: 100%;

  @media (min-width: 400px) {
    width: ${({ businessPlanIsEnabled }) =>
      businessPlanIsEnabled ? '292px' : '368px'};
  }

  height: ${({ isTeamsContainer, height }) =>
    height ? height : !isTeamsContainer ? '634px' : 'unset'};
  scroll-snap-align: center;
  border-radius: ${themeStyle.radiusLarge};
  box-shadow: none;
  background-color: ${themeStyle.varBackgroundAlt};
  z-index: ${({ state }) =>
    state === 'highlighted' ? themeStyle.zIndexS1 : 0};

  margin: 12px auto;

  ${QUERIES.tabletAndUp} {
    width: ${({ businessPlanIsEnabled }) =>
      businessPlanIsEnabled ? '292px' : '368px'};
    margin: 14px 0 24px 0;
  }
`;
const listStyles = css`
  ul {
    list-style-type: none;
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    text-align: start;
  }
`;

export const PlanContent = styled.div<{
  hasLabel: boolean;
  center?: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  padding: 32px 24px;

  ${({ center }) =>
    center &&
    `
    display: flex;
    flex-direction: column;
    justify-content: center;
  `};

  border-radius: ${themeStyle.radiusLarge};
  border: ${({ hasLabel }) => `${getBorder(hasLabel)};`};
  border-top-width: 2px;

  html[data-theme='dark'] &&,
  .dark && {
    border-color: ${({ hasLabel }) =>
      hasLabel ? `${themeStyle.darkHighlight}66` : themeStyle.varInkLight};
  }

  ${listStyles}
`;
export const PriceLabel = styled.div`
  display: flex;
  align-items: end;
  gap: 6px;
  font-size: ${themeStyle.fontSize11};
  font-weight: ${themeStyle.fontMedium};
  color: ${themeStyle.varInkMain};
  padding: 0;
  margin: 0;

  em {
    font-size: ${themeStyle.fontSize36};
    line-height: ${themeStyle.fontSize36};
    font-weight: ${themeStyle.fontExtraBold};
    font-style: normal;
  }
`;

export const PriceCustomLabel = styled.div`
  color: ${themeStyle.varInkMain};
  font-size: ${themeStyle.fontSize36};
  line-height: ${themeStyle.fontSize36};
  font-weight: ${themeStyle.fontExtraBold};
`;

/** @knipignore - false positive */
export const FeatureLabel = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: ${themeStyle.varInkMain};
`;

export const Feature = styled.li`
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 0px;
  }

  span {
    display: block;
    font-weight: ${themeStyle.fontMedium};
    font-size: ${themeStyle.fontSize14};
    color: ${themeStyle.varInkMain};
    line-height: 22px;
  }

  .content {
    margin-left: 6px;
  }

  .label {
    display: flex;
    align-items: center;

    .icon {
      width: 12px;
      height: 12px;
      overflow: visible;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-left: 8px;
    }
  }

  .details {
    font-weight: ${themeStyle.fontMedium};
  }
`;

export const LabelWrapper = styled.div`
  height: 39px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${themeStyle.highlight};
  border-radius: ${themeStyle.radiusLarge} ${themeStyle.radiusLarge} 0 0;
`;

export const OfferBadge = styled.div`
  position: relative;
  background: ${themeStyle.alertRed};
  border-radius: 10px;
  padding: 3px 8px;
  font-size: 12px;
  margin-left: 8px;
  width: fit-content;
  font-weight: ${themeStyle.fontSemiBold};
  line-height: 13px;
  color: ${themeStyle.inkBase};
  margin-bottom: 4px;
`;

export const Strikethrough = styled.div`
  position: absolute;
  left: 20%;
  width: 60%;
  top: 45%;
  height: 0.5px;

  background: ${themeStyle.inkBase};
`;

export const Features = styled.div`
  width: 100%;
  display: flex;
  justify-content: start;

  ${listStyles}

  ul {
    display: flex;
    width: 100%;
    gap: 12px;
  }
`;

export const ContentPlanBadge = styled.img.attrs(() => ({
  src: '/images/contentPlan.png',
}))`
  width: 128px;
  height: auto;

  position: absolute;
  top: 0;
  right: 0;
`;

export const InfoIconWrapper = styled.i`
  margin-left: 6px;
  & circle {
    stroke: ${themeStyle.varInkMedium};
  }
`;

// @TODO Find a way to share this BadgeContainer with the one in website
// https://app.clickup.com/t/86bzfdp9f
/** @knipignore -- used in website */
export const BadgeContainer = styled(CenterChildren)`
  box-sizing: border-box;
  padding: 5px 8px;
  border-radius: ${themeStyle.radiusSmall};
  background: ${themeStyle.varBrandLight};
  color: ${themeStyle.varBrandMain};
  font-weight: ${themeStyle.fontMedium};
  font-size: ${themeStyle.fontSize11};
  html[data-theme='dark'] &&,
  .dark && {
    background: ${themeStyle.varBrandSuperLight};
  }
`;
